import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  GameState,
  Question,
  HighScore,
  QuestionSource,
  QuestionBank,
} from "./types";
import { QUESTIONS_BY_DIFFICULTY } from "./database";
import StartScreen from "./components/StartScreen";
import GameScreen from "./components/GameScreen";
import EndScreen from "./components/EndScreen";
import useSounds from "./hooks/useSounds";
import {
  RestartIcon,
  SpeakerOnIcon,
  SpeakerOffIcon,
  TrophyIcon,
} from "./components/icons";
import HighScoresModal from "./components/HighScoresModal";
import BreakScreen from "./components/BreakScreen";

// Helper to shuffle an array
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const App: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>("start");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);

  // Audio state
  const [isMuted, setIsMuted] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const sounds = useSounds(isMuted);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Refs to track state inside event listeners without re-binding
  const gameStateRef = useRef(gameState);
  const userInteractedRef = useRef(userInteracted);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    userInteractedRef.current = userInteracted;
  }, [userInteracted]);

  const [highScores, setHighScores] = useState<HighScore[]>([]);
  const [showHighScores, setShowHighScores] = useState(false);

  // Question Bank State
  const [questionBank, setQuestionBank] = useState<QuestionBank>(
    QUESTIONS_BY_DIFFICULTY
  );

  // Load data from localStorage
  useEffect(() => {
    try {
      const savedScores = localStorage.getItem("highScores");
      if (savedScores) setHighScores(JSON.parse(savedScores) as HighScore[]);

      const savedQuestions = localStorage.getItem("questionBank");
      if (savedQuestions) {
        setQuestionBank(JSON.parse(savedQuestions) as QuestionBank);
      }
    } catch (error) {
      console.error("Failed to parse data from localStorage", error);
    }
  }, []);

  // Force load audio metadata on mount to help mobile browsers prep the buffer
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, []);

  // GLOBAL AUDIO UNLOCK: Listen for the very first interaction
  useEffect(() => {
    if (userInteracted) return;

    const handleInteraction = () => {
      if (userInteractedRef.current) return;

      setUserInteracted(true);
      sounds.initAudio();

      // Try to play background music immediately on interaction if condition is met.
      // This ensures we capture the user gesture synchronously.
      // Only play if on Start, Break, or Finished screen.
      const shouldPlay =
        !isMuted &&
        (gameStateRef.current === "start" ||
          gameStateRef.current === "break" ||
          gameStateRef.current === "finished");

      if (shouldPlay && audioRef.current) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Suppress autoplay errors
          });
        }
      }
    };

    const opts = { once: true };
    document.addEventListener("click", handleInteraction, opts);
    document.addEventListener("keydown", handleInteraction, opts);
    document.addEventListener("touchstart", handleInteraction, opts);

    return () => {
      document.removeEventListener("click", handleInteraction);
      document.removeEventListener("keydown", handleInteraction);
      document.removeEventListener("touchstart", handleInteraction);
    };
  }, [userInteracted, isMuted, sounds]);

  // Sync audio playback with game state and mute status
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      // Music should only play on 'start', 'break', or 'finished' screens
      const shouldPlay =
        userInteracted &&
        !isMuted &&
        (gameState === "start" ||
          gameState === "break" ||
          gameState === "finished");

      if (shouldPlay) {
        if (audio.paused) {
          audio.play().catch(() => {});
        }
      } else {
        if (!audio.paused) {
          audio.pause();
        }
      }
    }
  }, [gameState, isMuted, userInteracted]);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    sounds.initAudio();

    // If this is the first interaction, we want to register it to unlock audio.
    if (!userInteracted) {
      setUserInteracted(true);
      // If the audio was technically "On" (default state) but waiting for interaction,
      // we should just let it play instead of toggling it to Off.
      if (!isMuted) {
        return;
      }
    }

    setIsMuted(!isMuted);
  };

  const startGame = useCallback(() => {
    if (!userInteracted) {
      setUserInteracted(true);
      sounds.initAudio();
    }

    // Round 1: 4 Easy, 3 Medium
    const easyQuestions = shuffleArray(questionBank.Easy).slice(0, 4);
    const mediumQuestions = shuffleArray(questionBank.Medium).slice(0, 3);
    // Round 2: 7 Hard
    const hardQuestions = shuffleArray(questionBank.Hard).slice(0, 7);

    const selectedSources: QuestionSource[] = [
      ...easyQuestions,
      ...mediumQuestions,
      ...hardQuestions,
    ];

    const gameQuestions: Question[] = selectedSources.map((source, index) => {
      const options = shuffleArray([
        ...source.incorrectAnswers,
        source.correctAnswer,
      ]);
      const correctAnswerIndex = options.findIndex(
        (option) => option === source.correctAnswer
      );

      return {
        id: index,
        text: source.text,
        difficulty: source.difficulty,
        options: options,
        correctAnswerIndex: correctAnswerIndex,
        explanation: source.explanation,
      };
    });

    setQuestions(gameQuestions);
    setScore(0);
    setStreak(0);
    setCurrentQuestionIndex(0);
    setGameState("playing");
  }, [sounds, questionBank, userInteracted]);

  const handleAnswer = useCallback(
    (points: number, isCorrect: boolean) => {
      setScore((prev) => prev + points);

      if (isCorrect) {
        setStreak((prev) => prev + 1);
      } else {
        setStreak(0);
      }

      if (currentQuestionIndex === 6) {
        // End of Round 1
        setGameState("break");
      } else if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        setGameState("finished");
      }
    },
    [currentQuestionIndex, questions.length]
  );

  const continueToRound2 = () => {
    sounds.initAudio();
    setCurrentQuestionIndex((prev) => prev + 1);
    setGameState("playing");
  };

  const addHighScore = useCallback(
    (name: string, score: number) => {
      const newScore = { name, score };
      const updatedScores = [...highScores, newScore]
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);

      setHighScores(updatedScores);
      localStorage.setItem("highScores", JSON.stringify(updatedScores));
    },
    [highScores]
  );

  const handleStartOver = useCallback(() => {
    setGameState("start");
    setScore(0);
    setStreak(0);
    setCurrentQuestionIndex(0);
    setQuestions([]);
  }, []);

  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in {
      animation: fadeIn 0.5s ease-out forwards;
    }
  `;

  const renderContent = () => {
    switch (gameState) {
      case "start":
        return <StartScreen onStart={startGame} />;
      case "playing":
        return (
          <GameScreen
            key={currentQuestionIndex}
            question={questions[currentQuestionIndex]}
            totalQuestions={questions.length}
            currentQuestionIndex={currentQuestionIndex}
            streak={streak}
            onAnswer={handleAnswer}
            sounds={sounds}
          />
        );
      case "break":
        return <BreakScreen score={score} onContinue={continueToRound2} />;
      case "finished":
        return (
          <EndScreen
            score={score}
            totalQuestions={questions.length}
            onRestart={startGame}
            highScores={highScores}
            addHighScore={addHighScore}
          />
        );
      default:
        return <StartScreen onStart={startGame} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
      <style>{animationStyles}</style>
      {/* Using raw=1 for direct streaming */}
      <audio
        ref={audioRef}
        src="https://www.dropbox.com/scl/fi/qq48wz25qhkeu3fnzn35i/ChemCorner-background.mp3?rlkey=xi900jmdvkuntfv3xa9lgpd7g&raw=1"
        loop
        playsInline
        preload="auto"
      />
      <div className="fixed top-4 right-4 z-10 flex items-center gap-2">
        {gameState !== "start" && (
          <button
            onClick={handleStartOver}
            className="p-2 bg-white/70 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
            aria-label="Start Over"
          >
            <RestartIcon />
          </button>
        )}
        <button
          onClick={() => setShowHighScores(true)}
          className="p-2 bg-white/70 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
          aria-label="View high scores"
        >
          <TrophyIcon />
        </button>
        <button
          onClick={toggleMute}
          className="p-2 bg-white/70 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
          aria-label={isMuted ? "Unmute audio" : "Mute audio"}
        >
          {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
        </button>
      </div>

      {showHighScores && (
        <HighScoresModal
          scores={highScores}
          onClose={() => setShowHighScores(false)}
        />
      )}

      <div className="w-full">{renderContent()}</div>
    </div>
  );
};

export default App;
